import { MaterialIcons } from "~/app/common/Icon";
import { CostCategory, Customer } from "~/types";
import { SaveWorkOrderPayload, WorkOrderDetail, WorkOrderStatuses } from "~/workorder/types";
import {
    isWorkOrderSelectValueSet,
    isWorkOrderSortingTypeSet,
} from "~/workorder/Detail/Administrative/adminFormHelper";
import {
    isOperatingCountRequired,
    isOperatingTimeRequired,
} from "~/workorder/Detail/OperatingTime/helper";

export enum WorkOrderFooterAction {
    PutOnHold = 'putOnHold',
    Continue = 'continue',
    Start = 'start',
    ConvertToOpen = 'convertToOpen',
    Cancel = 'cancel',
    Delete = 'delete',
    Save = 'save',
    Complete = 'complete',
}

export const getFooterActionIcon = (action: WorkOrderFooterAction): MaterialIcons => {
    switch (action) {
        case WorkOrderFooterAction.Start:
        case WorkOrderFooterAction.Continue:
            return 'play_arrow';
        case WorkOrderFooterAction.PutOnHold:
            return 'pause_circle';
        case WorkOrderFooterAction.ConvertToOpen:
            return 'loop';
        case WorkOrderFooterAction.Complete:
            return 'check_circle';
        case WorkOrderFooterAction.Save:
            return 'save';
        case WorkOrderFooterAction.Cancel:
            return 'close';
        case WorkOrderFooterAction.Delete:
            return 'delete_outline';
        default:
            return 'more_horiz';
    }
};

export const getButtonColor = (action: WorkOrderFooterAction): 'primary' | 'red' | 'green' | 'white' | undefined => {
    if (action === WorkOrderFooterAction.Delete) {
        return 'red';
    }

    if (action === WorkOrderFooterAction.Start || action === WorkOrderFooterAction.Continue || action === WorkOrderFooterAction.Complete) {
        return 'green';
    }

    if (action === WorkOrderFooterAction.Save || action === WorkOrderFooterAction.ConvertToOpen) {
        return 'primary';
    }

    if (action === WorkOrderFooterAction.PutOnHold || action === WorkOrderFooterAction.Cancel) {
        return 'white';
    }
    return undefined;
};

export const FOOTER_ACTION_ORDER: WorkOrderFooterAction[] = [
    WorkOrderFooterAction.Start,
    WorkOrderFooterAction.Continue,
    WorkOrderFooterAction.PutOnHold,
    WorkOrderFooterAction.ConvertToOpen,
    WorkOrderFooterAction.Complete,
    WorkOrderFooterAction.Save,
    WorkOrderFooterAction.Cancel,
    WorkOrderFooterAction.Delete,
];

export const isWorkOrderFooterActionVisible = (
    action: WorkOrderFooterAction,
    status: WorkOrderStatuses,
): boolean => {
    switch (action) {
        case WorkOrderFooterAction.PutOnHold:
            return status === WorkOrderStatuses.INPROGRESS;
        case WorkOrderFooterAction.Continue:
            return status === WorkOrderStatuses.ONHOLD;
        case WorkOrderFooterAction.Start:
            return status === WorkOrderStatuses.PLANNED || status === WorkOrderStatuses.DELAYED;
        // TODO: will added Next Phase
        // case WorkOrderFooterAction.ConvertToOpen:
        //     return (
        //         status === WorkOrderStatuses.PLANNED
        //         || status === WorkOrderStatuses.ONHOLD
        //         || status === WorkOrderStatuses.DELAYED
        //     );
        // case WorkOrderFooterAction.Cancel:
        //     return status !== WorkOrderStatuses.APPROVED && status !== WorkOrderStatuses.CANCELLED;
        // case WorkOrderFooterAction.Delete:
        //     return status < WorkOrderStatuses.FINISHED || status === WorkOrderStatuses.NOTAPPROVED;
        case WorkOrderFooterAction.Save:
            return status !== WorkOrderStatuses.APPROVED;
        case WorkOrderFooterAction.Complete:
            return (
                (
                    status > WorkOrderStatuses.PLANNED
                    && status < WorkOrderStatuses.FINISHED
                )
                || status === WorkOrderStatuses.NOTAPPROVED
            );
        default:
            return false;
    }
};

export type WorkOrderTaskProgress = {
    total: number;
    done: number;
};

export const areAllWorkOrderTasksDone = (taskProgress: WorkOrderTaskProgress): boolean => (
    taskProgress.total === 0 || taskProgress.done === taskProgress.total
);

export const getVisibleWorkOrderFooterActions = (
    status: WorkOrderStatuses,
): WorkOrderFooterAction[] => (
    FOOTER_ACTION_ORDER.filter(action => isWorkOrderFooterActionVisible(action, status))
);

export const getTargetStatusForFooterAction = (
    action: WorkOrderFooterAction,
): WorkOrderStatuses | null => {
    switch (action) {
        case WorkOrderFooterAction.Start:
        case WorkOrderFooterAction.Continue:
            return WorkOrderStatuses.INPROGRESS;
        case WorkOrderFooterAction.PutOnHold:
            return WorkOrderStatuses.ONHOLD;
        case WorkOrderFooterAction.Complete:
            return WorkOrderStatuses.FINISHED;
        default:
            return null;
    }
};

export const buildSaveWorkOrderPayload = (detail: WorkOrderDetail): SaveWorkOrderPayload => ({
    workOrderId: detail.id,
    status: detail.status as WorkOrderStatuses,
    scheduledStartDate: detail.scheduledStartDate,
    scheduledEndDate: detail.scheduledEndDate,
});

export { isWorkOrderSelectValueSet, isWorkOrderSortingTypeSet } from '~/workorder/Detail/Administrative/adminFormHelper';
export {
    isOperatingCountRequired,
    isOperatingTimeRequired,
} from '~/workorder/Detail/OperatingTime/helper';

export const isWorkOrderScheduledDateSet = (value: string | null | undefined): boolean => (
    String(value ?? '').trim() !== ''
);

export const isWorkOrderOperatingTimeSet = (value: number | string | null | undefined): boolean => (
    value !== null && value !== undefined && String(value).trim() !== '' && Number(value) > 0
);

export const isWorkOrderOperatingCountSet = (value: number | string | null | undefined): boolean => (
    value !== null && value !== undefined && String(value).trim() !== '' && Number(value) > 0
);

export const validateWorkOrderOperatingTime = (detail: WorkOrderDetail): boolean => {
    if (!isOperatingTimeRequired(detail.excavator)) {
        return true;
    }
    return isWorkOrderOperatingTimeSet(detail.operatingTime);
};

export const validateWorkOrderOperatingCount = (detail: WorkOrderDetail): boolean => {
    if (!isOperatingCountRequired(detail.excavator)) {
        return true;
    }
    return isWorkOrderOperatingCountSet(detail.operatingCount);
};

export type WorkOrderAdministrativeValidationError =
    | 'scheduledStartDateRequired'
    | 'scheduledEndDateRequired'
    | 'scheduledEndBeforeStart'
    | 'costCategoryRequired'
    | 'chargedDepartmentRequired'
    | 'customerRequired'
    | 'projectRequired'
    | 'sortingRequired';

export const workOrderFooterActionsRequiringAdministrativeValidation: WorkOrderFooterAction[] = [
    WorkOrderFooterAction.Start,
    WorkOrderFooterAction.Continue,
    WorkOrderFooterAction.PutOnHold,
    WorkOrderFooterAction.Save,
    WorkOrderFooterAction.Complete
];

export const isWorkOrderAdministrativeValidationRequired = (
    action: WorkOrderFooterAction,
): boolean => workOrderFooterActionsRequiringAdministrativeValidation.includes(action);

export const validateWorkOrderAdministrativeFields = (
    detail: WorkOrderDetail,
    costCategories: CostCategory[],
    customers: Customer[],
): WorkOrderAdministrativeValidationError | null => {
    if (!isWorkOrderScheduledDateSet(detail.scheduledStartDate)) {
        return 'scheduledStartDateRequired';
    }

    if (!isWorkOrderScheduledDateSet(detail.scheduledEndDate)) {
        return 'scheduledEndDateRequired';
    }

    const startDate = detail.scheduledStartDate ? new Date(detail.scheduledStartDate) : null;
    const endDate = detail.scheduledEndDate ? new Date(detail.scheduledEndDate) : null;
    if (startDate && endDate && endDate < startDate) {
        return 'scheduledEndBeforeStart';
    }

    if (!isWorkOrderSelectValueSet(detail.costCategoryId)) {
        return 'costCategoryRequired';
    }

    const category = costCategories.find(
        ({ id }) => Number(id) === Number(detail.costCategoryId),
    );

    if (category?.chargeDepartment && !isWorkOrderSelectValueSet(detail.chargedDepartmentId)) {
        return 'chargedDepartmentRequired';
    }

    if (category?.displayCustomer && !isWorkOrderSelectValueSet(detail.customerId)) {
        return 'customerRequired';
    }

    const customer = customers.find(
        ({ id }) => Number(id) === Number(detail.customerId),
    );

    if (customer?.displayProject && !isWorkOrderSelectValueSet(detail.projectId)) {
        return 'projectRequired';
    }

    if (customer?.needsSorting && !isWorkOrderSortingTypeSet(detail.sortingType)) {
        return 'sortingRequired';
    }

    return null;
};